import { FC, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { getLocationTitle, shouldShowFieldValue } from 'entities/kyrgyzstan'

import {
  DynamicFieldValue,
  RealtyImage,
  RealtyType,
} from 'shared/api/kyrgyzstan/search'
import {
  DynamicField,
  RealtyCity,
  RealtySource,
} from 'shared/api/kyrgyzstan/search/model'
import { CurrencySom } from 'shared/constants'
import GhostImage from 'shared/images/ghost.png'
import { priceShow } from 'shared/lib/priceShow'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { Carousel } from 'shared/ui/carousel'
import { MBonusDiscount } from 'shared/ui/mbonus/mbonus-discount'

import { TypeTag } from '../type-tag'

import s from './styles.module.scss'

const MaxPopularDynamicFields = 3

export interface KyrgyzstanCardProps {
  id: string
  title: string
  address: string
  city: RealtyCity
  images: RealtyImage[]
  type: RealtyType
  totalPrice: number
  pricePerNight: number
  totalNights: number
  popularDynamicFields: DynamicField[]
  dynamicFields: DynamicFieldValue
  mbonus_available: boolean
  source: RealtySource
  onClick?: (id: string) => void
}

export const KyrgyzstanCard: FC<KyrgyzstanCardProps> = ({
  id,
  title,
  address,
  city,
  images,
  type,
  totalPrice,
  pricePerNight,
  totalNights,
  popularDynamicFields,
  dynamicFields,
  mbonus_available,
  source,
  onClick,
}) => {
  const { t } = useTranslation()

  const preparedImages = useMemo(() => {
    return images.map((image) => ({
      id: image.id,
      image: image.image_url,
      image_fullpath: image.image_url,
    }))
  }, [images])

  const shouldShowDynamicFields = popularDynamicFields.length > 0
  const slicedPopularDynamicFields = popularDynamicFields.slice(
    0,
    MaxPopularDynamicFields,
  )
  const moreFieldsCount =
    dynamicFields.count - slicedPopularDynamicFields.length

  const handleClick = () => {
    onClick?.(id)
  }

  return (
    <div
      className={s.card}
      onClick={handleClick}
    >
      <div className={s.imageWrapper}>
        <Carousel
          dotsVariant="custom"
          images={preparedImages}
          errorImage={GhostImage}
          showCounter
          size="aspect"
        />

        <TypeTag
          type={type}
          className={s.typeTag}
        />
      </div>

      <div className={s.content}>
        <h3 className={clsx('extra-bold-16', s.title)}>{title}</h3>

        <p className={clsx('semi-bold-12', s.location)}>
          {getLocationTitle(city, address, source)}
        </p>

        {shouldShowDynamicFields && (
          <div className={s.serviceValue}>
            {popularDynamicFields
              .slice(0, MaxPopularDynamicFields)
              .map((field) => (
                <div
                  key={field.id}
                  className={s.serviceValueItem}
                >
                  <AppIcon
                    id={AllIcons.CHECK_MARK}
                    width={12}
                    height={12}
                    className={s.serviceValueIcon}
                  />
                  <span className={clsx('medium-12', s.serviceValueLabel)}>
                    {field.name}
                    {shouldShowFieldValue(field) && (
                      <span className={s.serviceValueValue}>
                        {': '}
                        {field.value}
                      </span>
                    )}
                  </span>
                </div>
              ))}

            {moreFieldsCount > 0 && (
              <div className={s.serviceValueItem}>
                <span className={clsx('medium-12', s.serviceValueLabel)}>
                  +{moreFieldsCount}
                </span>
              </div>
            )}
          </div>
        )}
        <div className={s.divider} />
        <div className={s.priceBlock}>
          <p className={clsx('extra-bold-16', s.totalPrice)}>
            {priceShow(String(totalPrice))} {CurrencySom}
          </p>
          <p className={clsx('semi-bold-12', s.priceBreakdown)}>
            (
            {t('kyrgyzstan_block.card.for_nights', {
              count: totalNights,
              nights: t('kyrgyzstan_block.card.nights', { count: totalNights }),
            })}
            , {priceShow(String(pricePerNight))} {CurrencySom}{' '}
            {t('kyrgyzstan_block.card.per_night')})
          </p>
        </div>
        {mbonus_available && (
          <div className={s.mbonus}>
            <MBonusDiscount
              title={t('kyrgyzstan_block.search_result.cashback', {
                percent: '10',
              })}
            />
          </div>
        )}
      </div>
    </div>
  )
}
